import { useCallback, useMemo } from "react";

import { useTrackNavClick } from "components/layout/shell/AppSidebar/useTrackNavClick";
import {
  canGenerateProjectStep,
  getProjectStep,
  resolveProjectStepStatus,
  type ProjectStepId,
  type ProjectStepStatus,
} from "config/navigation";
import {
  useOpenProjectScriptAiHub,
  type RunAiHubPipeline,
} from "features/script-editor/script-ai-hub/projectScriptAiHubContext";
import { useWriterStudioUpgrade } from "hooks/project/useWriterStudioUpgrade";
import { useProjectIdentifiers } from "store/hooks/useProjectIdentifiers";
import {
  resolveRunAiHubPipelineVisibility,
  type RunAiHubPipelineFlags,
} from "utils/runAiHubPipelineVisibility";

/** The Run AI row each step's Generate button opens. */
const STEP_PIPELINE: Record<ProjectStepId, RunAiHubPipeline> = {
  script: "script_breakdown",
  characters: "character_breakdown",
  schedule: "schedule",
  budget: "budget",
};

const PIPELINE_FLAG: Record<RunAiHubPipeline, keyof RunAiHubPipelineFlags> = {
  script_breakdown: "hasScriptBreakdown",
  character_breakdown: "hasCharacterBreakdown",
  coverage: "hasCoverage",
  schedule: "hasSchedule",
  budget: "hasBudget",
};

export interface StepGeneration {
  status: ProjectStepStatus | null;
  /** The pipeline never ran on the current draft. False while the project loads. */
  notGenerated: boolean;
  /** Opens Run AI on the pipeline, or null when the user cannot run it here. */
  generate: (() => void) | null;
}

/**
 * Whether a step page should show its "not generated" empty state, and the
 * action for its Generate button. `pipeline` overrides the step's own, for
 * pages with their own pipeline (Coverage under Script).
 *
 * The same status rules as the sidebar decide "not generated". The button
 * only opens the Run AI dialog with the pipeline preselected; the user still
 * clicks Run there.
 */
export function useStepGeneration(
  stepId: ProjectStepId,
  pipeline?: RunAiHubPipeline
): StepGeneration {
  const { project } = useProjectIdentifiers();
  const openRunAiHub = useOpenProjectScriptAiHub();
  const trackNavClick = useTrackNavClick();
  const studio = useWriterStudioUpgrade(
    project ? String(project.projectId) : undefined,
    project ?? null
  );
  const step = getProjectStep(stepId);
  const target = pipeline ?? STEP_PIPELINE[stepId];

  const status = useMemo<ProjectStepStatus | null>(() => {
    if (!project) return null;
    const definition = pipeline ? { primaryFeature: pipeline, secondaryFeatures: [] } : step;
    return resolveProjectStepStatus(definition, project.featureStatuses, project.activeDocumentId);
  }, [pipeline, project, step]);

  // A Writer project on a Studio step keeps its lock and upsell as they are.
  const locked = step.studioOnly && studio.needsStudioUpgrade;
  const canGenerate =
    status !== null &&
    canGenerateProjectStep({
      status,
      locked,
      canRunAi: Boolean(openRunAiHub),
      pipelineAvailable: resolveRunAiHubPipelineVisibility(project ?? null)[PIPELINE_FLAG[target]],
    });

  const generate = useCallback(() => {
    if (!openRunAiHub) return;
    trackNavClick({ area: "empty_state", step: stepId, item: `generate-${target}` });
    openRunAiHub(target);
  }, [openRunAiHub, stepId, target, trackNavClick]);

  return {
    status,
    notGenerated: status === "not_started" && !locked,
    generate: canGenerate ? generate : null,
  };
}
